import React from 'react';
import { PropTypes } from 'prop-types';
import { Field, Form } from 'react-final-form';
import moment from 'moment';
import { NoteDisclosure, validations } from '@unite-us/app-components';
import { TextField } from '@unite-us/ui';
import DialogV2 from 'common/modal/DialogV2';
import useAuthorizationFormData from '../shared/useAuthorizationFormData';
import {
  DATE_TYPE_VALUES,
  validateAuthorizationForm,
  buildFormInitialValues,
  closeModal,
} from '../../../utils/serviceAuthorizationUtils';
import { buildSubmitPayload } from '../shared/buildSubmitPayload';
import DefaultAuthorizationFormFields from '../shared/DefaultAuthorizationFormFields';
import AuthorizationSummarySection from './AuthorizationSummarySection';
import AmountAvailableSection from './AmountAvailableSection';

const RequestAuthorizationChangesModal = ({
  authorization,
  modalRef,
  isUnitBased,
  onSubmit,
}) => {
  const {
    feeScheduleProgram,
    person,
    isCostBased,
    isAmountExceeded,
    setIsAmountExceeded,
    totalInvoicedSpend,
    invoicedSpendRecords,
    isInvoicedSpendLoaded,
    initialClinicalModificationCodeValue,
  } = useAuthorizationFormData(authorization);

  const formInitialValues = buildFormInitialValues(authorization, {
    prefix: 'approved',
    isUnitBased,
    extraFields: {
      update_request_note: '',
      clinical_modification_code: initialClinicalModificationCodeValue,
    },
  });

  const handleSubmit = async (formData, form) => {
    const payload = buildSubmitPayload(formData, { isUnitBased, prefix: 'requested' });
    await onSubmit({
      update_request_note: formData.update_request_note,
      payer_authorization_number: authorization.payer_authorization_number,
      ...payload,
    });
    closeModal(modalRef, form.reset);
  };

  return (
    <Form
      keepDirtyOnReinitialize
      initialValues={formInitialValues}
      onSubmit={handleSubmit}
      validate={(values) => validateAuthorizationForm(values, {
        isAmountExceeded,
        isUnitBased,
        isInvoicedSpendLoaded,
        totalInvoicedSpend,
        invoicedSpendRecords,
        authorization,
      })}
      render={({
        error,
        form,
        handleSubmit: formHandleSubmit,
        valid,
        values: {
          authorized_amount,
          date_type,
          range_date_start,
          range_date_end,
          single_date,
        },
      }) => {
        const requestedStartsAt = moment.unix(
          date_type === DATE_TYPE_VALUES.RANGE_DATE ? range_date_start : single_date,
        ).utc();
        const requestedEndsAt = moment.unix(
          date_type === DATE_TYPE_VALUES.RANGE_DATE ? (range_date_end || range_date_start) : single_date,
        ).utc();

        return (
          <DialogV2
            ref={modalRef}
            title="Request Changes to Authorization"
            confirmLabel="Submit Request"
            cancelHandler={() => closeModal(modalRef, form.reset)}
            confirmationHandler={formHandleSubmit}
            confirmationBtnDisabled={!valid}
            dataTestId="request-change-authorization-modal"
            confirmButtonTestId="request-change-submit-button"
            width="3xl"
          >
            <p>
              The authorized amount and service delivery dates
              are in effect until requested changes are accepted.
            </p>
            <AuthorizationSummarySection
              authorization={authorization}
              isUnitBased={isUnitBased}
              feeScheduleProgram={feeScheduleProgram}
            />
            <hr className="my-5 border-dark-fill-blue border-solid" />
            <AmountAvailableSection
              feeScheduleProgram={feeScheduleProgram}
              person={person}
              isUnitBased={isUnitBased}
              requestedStartsAt={requestedStartsAt}
              requestedEndsAt={requestedEndsAt}
              authorizedAmount={authorized_amount}
              setIsAmountExceeded={setIsAmountExceeded}
              authorization={authorization}
            />
            <div className="grid grid-cols-2 m-0 gap-x-24">
              <DefaultAuthorizationFormFields
                dateType={date_type}
                error={error}
                isUnitBased={isUnitBased}
                isCostBased={isCostBased}
                feeScheduleProgram={feeScheduleProgram}
                amountLabel="AMOUNT REQUESTED"
              />

              <p className="italic text-medium-grey mb-4">
                This amount is in place of, not in addition to, the Authorized Amount.
              </p>

              <Field name="update_request_note" validate={(value) => validations.isRequired(value)}>
                {(props) => (
                  <TextField
                    {...props}
                    className="!p-0 col-span-2"
                    id="update_request_note"
                    label="NOTES"
                    afterLabelContent={<NoteDisclosure />}
                    required
                  />
                )}
              </Field>
            </div>
          </DialogV2>
        );
      }}
    />
  );
};

RequestAuthorizationChangesModal.propTypes = {
  authorization: PropTypes.shape({
    id: PropTypes.string.isRequired,
    original_service_authorization: PropTypes.string,
    fee_schedule_program: PropTypes.shape({
      fee_schedule: PropTypes.shape({
        id: PropTypes.string.isRequired,
      }),
    }).isRequired,
    approved_cents: PropTypes.number,
    approved_starts_at: PropTypes.string,
    approved_ends_at: PropTypes.string,
    approved_unit_amount: PropTypes.number,
    payer_authorization_number: PropTypes.string,
    case: PropTypes.shape({
      person: PropTypes.shape({
        id: PropTypes.string.isRequired,
      }),
    }),
    clinical_modifications: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        code: PropTypes.string.isRequired,
      }),
    ),
  }).isRequired,
  modalRef: PropTypes.shape({
    current: PropTypes.any,
  }).isRequired,
  isUnitBased: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default RequestAuthorizationChangesModal;
